'use client';

import type { Tables, TablesInsert, TablesUpdate } from '@root/database';
import { Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { createGame, updateGame } from '@/lib/games/actions';
interface GameDialogProps {
  game?: Tables<'game'>;
}
export const GameDialog = ({
  game
}: GameDialogProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    console: game?.console || '',
    help_card: (game as ({
      help_card?: string | null;
    } & Tables<'game'>) | undefined)?.help_card || '',
    image_url: game?.image_url || '',
    logo: (game as ({
      logo?: string | null;
    } & Tables<'game'>) | undefined)?.logo || '',
    name: game?.name || ''
  });
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      const data = {
        console: formData.console,
        help_card: formData.help_card || null,
        image_url: formData.image_url || null,
        logo: formData.logo || null,
        name: formData.name
      };
      if (game) {
        await updateGame(game.id, data as {
          help_card?: string | null;
          logo?: string | null;
        } & TablesUpdate<'game'>);
      } else {
        await createGame(data as {
          help_card?: string | null;
          logo?: string | null;
        } & TablesInsert<'game'>);
      }
      setOpen(false);
      setFormData({
        console: '',
        help_card: '',
        image_url: '',
        logo: '',
        name: ''
      });
      toast.success(game ? 'Jeu modifié' : 'Jeu créé');
    } catch (error) {
      console.error(error);
      toast.error('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };
  return <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {game ? <Button aria-label="Modifier le jeu" className="hover:bg-gray-100" size="icon" title="Modifier le jeu" variant="ghost">
            <Pencil className="h-4 w-4 text-gray-700" />
          </Button> : <Button>
            <Plus />
            Nouveau jeu
          </Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{game ? 'Modifier le jeu' : 'Nouveau jeu'}</DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="name">Nom du jeu</Label>
            <Input id="name" required value={formData.name} onChange={event_0 => setFormData({
            ...formData,
            name: event_0.target.value
          })} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="help_card">Carte d’aide (URL)</Label>
            <Input id="help_card" placeholder="https://..." value={formData.help_card} onChange={event_1 => setFormData({
            ...formData,
            help_card: event_1.target.value
          })} />
            <p className="text-xs text-gray-500">
              Image optionnelle affichée en appuyant sur “?” dans le bracket.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="console">Console</Label>
            <Input id="console" placeholder="Ex: Nintendo 64, PlayStation 5..." required value={formData.console} onChange={event_2 => setFormData({
            ...formData,
            console: event_2.target.value
          })} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="image_url">Image URL</Label>
            <Input id="image_url" placeholder="https://..." type="url" value={formData.image_url} onChange={event_3 => setFormData({
            ...formData,
            image_url: event_3.target.value
          })} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="logo">Logo (URL)</Label>
            <Input id="logo" placeholder="https://..." value={formData.logo} onChange={event_4 => setFormData({
            ...formData,
            logo: event_4.target.value
          })} />
            <p className="text-xs text-gray-500">
              URL optionnelle d’un logo qui sera affiché en bas du bracket du
              tournoi.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button disabled={loading} type="submit">
              {loading && <Spinner />}
              {game ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>;
};